import { useState } from "react";
import api, { errMsg, useList } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Mail, MessageCircle, ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { OnboardingJourneyPanel } from "@/components/onboarding/OnboardingJourneyPanel";

export default function AccountClaimsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");
  const { data: parents, loading, reload } = useList(`/admin/account-claims/parents?search=${encodeURIComponent(query)}`, [query]);

  const send = async (parent, channel) => {
    setBusy(`${parent.id}:${channel}`);
    try {
      await api.post("/admin/account-claims", { parent_id: parent.id, channel });
      toast.success(`Secure ${channel} claim invitation sent`);
      reload();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="account-claims-admin-page">
      <PageHeader title="Account Claims" description="Activate imported family profiles without generated passwords or duplicate accounts." />
      <div className="stage-dark rounded-lg p-6 text-white mb-6 flex gap-4 items-start" data-testid="account-claims-security-banner">
        <ShieldCheck className="h-6 w-6 text-secondary shrink-0" />
        <div><p className="font-display text-xl font-semibold">One link. One family. One use.</p>
          <p className="text-sm text-white/70 mt-1">Email uses the configured sandbox. WhatsApp stays allowlisted and requires the family to message Hi first.</p></div>
      </div>
      <OnboardingJourneyPanel onInvite={send} />
      <form onSubmit={(e) => { e.preventDefault(); setQuery(search); }} className="flex gap-2 mb-5 max-w-xl" data-testid="account-claims-search-form">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search unclaimed families" data-testid="account-claims-search-input" />
        <Button type="submit" variant="outline" data-testid="account-claims-search-button"><Search className="h-4 w-4" /></Button>
      </form>
      {loading ? <Spinner /> : !parents?.length ? <div data-testid="account-claims-empty-state"><EmptyState text="No unclaimed family profiles found." /></div> : (
        <div className="border bg-white rounded-lg overflow-hidden" data-testid="account-claims-family-list">
          {parents.map((parent) => (
            <div key={parent.id} className="grid md:grid-cols-[1.2fr_1fr_auto] gap-4 p-5 border-b last:border-0" data-testid={`account-claim-family-${parent.id}`}>
              <div><div className="flex items-center gap-2"><p className="font-semibold">{parent.full_name}</p><Badge variant="outline">Unclaimed</Badge></div>
                <p className="text-sm text-muted-foreground mt-1" data-testid={`account-claim-contact-${parent.id}`}>{parent.email || "No email"} · {parent.phone || "No WhatsApp"}</p></div>
              <div><p className="text-xs font-semibold uppercase text-muted-foreground">Students</p>
                <p className="text-sm mt-1" data-testid={`account-claim-students-${parent.id}`}>{parent.students?.map((s) => s.full_name).join(" · ") || "No linked students"}</p>
                {parent.active_claim && <p className="text-xs text-emerald-700 mt-1" data-testid={`account-claim-active-${parent.id}`}>Active {parent.active_claim.channel} link · expires {new Date(parent.active_claim.expires_at).toLocaleTimeString()}</p>}</div>
              <div className="flex md:justify-end items-center gap-2">
                <Button size="sm" variant="outline" disabled={!parent.email || !!busy} onClick={() => send(parent, "email")} data-testid={`account-claim-send-email-${parent.id}`}><Mail className="h-4 w-4 mr-1" /> Email</Button>
                <Button size="sm" disabled={!parent.phone || !!busy} onClick={() => send(parent, "whatsapp")} data-testid={`account-claim-send-whatsapp-${parent.id}`}><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}